import styled, { css } from 'styled-components';
import cellCross from '../../assets/images/cell_cross.svg';
import { TextLabel } from '../TextLabel';

const StyledWrapper = styled.div<{
	$width: number;
	$height: number;
}>`
	position: relative;

	${(props) =>
		props.$width > 1 &&
		css`
			grid-column: span ${props.$width};
		`}

	${(props) =>
		props.$height > 1 &&
		css`
			grid-row: span ${props.$height};
		`}
`;

const StyledCountContainer = styled.div<{ $open?: boolean }>`
	position: absolute;
	top: 8px;
	right: 8px;
	z-index: 3;
	display: flex;
	align-items: center;
	justify-content: center;
	height: 16px;
	padding: 0 4.5px;
	background-color: #ffffff;
	border-radius: 5px;
	transition:
		background-color 0.3s ease,
		filter 0.3s ease;

	${TextLabel} {
		font-weight: 700;
		font-size: 10px;
		color: #000000;
		transition: color 0.3s ease;
	}
`;

const StyledPlaceholderContainer = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 9px;
	height: 100%;

	img {
		width: 40px;
		height: 40px;
		user-select: none;
		pointer-events: none;
		opacity: 0.1;
		transition: opacity 0.3s ease;
	}

	${TextLabel} {
		font-size: 10px;
		color: #ffffff;
		opacity: 0.25;
		transition: opacity 0.3s ease;
	}
`;

const StyledContainer = styled.div<{
	$width: number;
	$height: number;
	$active?: boolean;
	$activeHand?: boolean;
	$disabled?: boolean;
	$closed?: boolean;
	$hint?: boolean;
	$help?: boolean;
	$helpHover?: boolean;
	$dragHint?: boolean;
}>`
	position: relative;
	width: ${(props) => 80 * props.$width}px;
	height: ${(props) => 80 * props.$height + (props.$height - 1) * 10}px;
	border-radius: 10px;
	z-index: ${(props) => (props.$hint ? 6 : 3)};
	transition:
		background 0.3s ease,
		border 0.3s ease;

	${(props) =>
		props.$disabled || props.$closed
			? css`
					border: 1px solid rgba(255, 255, 255, 0.05);

					${StyledCountContainer} {
						filter: brightness(0.25);
					}
			  `
			: css`
					border: 1px solid rgba(255, 255, 255, 0.1);

					${props.$active
						? css`
								background: rgba(255, 255, 255, 0.1);

								${!props.$hint &&
								css`
									&:hover {
										background-color: #ffffff;
										border: 1px solid #ffffff;

										${StyledCountContainer} {
											background-color: #000000;

											${TextLabel} {
												color: #ffffff;
											}
										}

										${StyledQuickAccessButton} {
											background-color: rgba(0, 0, 0, 0.1);

											${TextLabel} {
												color: #000000;
											}
										}
									}
								`}
						  `
						: css`
								background: rgba(255, 255, 255, 0.02);
						  `}

					${props.$activeHand &&
					css`
						background: #2d70da;
					`}

					${props.$dragHint &&
					css`
						background: rgba(255, 255, 255, 0.1);
						border: 1px solid rgba(255, 255, 255, 0.5);
					`}
			  `}

	${(props) =>
		props.$hint &&
		css`
			pointer-events: none;

			${StyledItemImageContainer} {
				pointer-events: all;
			}
		`}

	${(props) =>
		props.$help &&
		!props.$hint &&
		css`
			background: rgba(255, 255, 255, 0.1);
			border: 1px solid rgba(255, 255, 255, 0.5);

			${StyledPlaceholderContainer} {
				img {
					opacity: 0.25;
				}

				${TextLabel} {
					opacity: 0.5;
				}
			}
		`}

	${(props) =>
		props.$helpHover &&
		css`
			background: rgba(255, 255, 255, 0.25);
			border: 1px solid rgba(255, 255, 255, 0.5);

			${StyledPlaceholderContainer} {
				img {
					opacity: 1;
				}

				${TextLabel} {
					opacity: 1;
				}
			}
		`}
`;

const StyledCross = styled.div`
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	width: 40px;
	height: 40px;
	background-image: url(${cellCross});
	background-repeat: no-repeat;
	background-position: center center;
	background-size: contain;
`;

const StyledItemImageContainer = styled.div<{ $disabled?: boolean }>`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 60px;
	height: 60px;
	margin: 9px 0 0 9px;
	${(props) =>
		props.$disabled &&
		css`
			img {
				opacity: 0.25;
			}
		`}
	transition: opacity 0.3s ease;
`;

const StyledQuickAccessButton = styled.div<{ $open?: boolean }>`
	position: absolute;
	top: 8px;
	left: 8px;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 16px;
	height: 16px;
	background-color: rgba(255, 255, 255, 0.1);
	border-radius: 4px;
	transition: background-color 0.3s ease;

	${TextLabel} {
		font-weight: 700;
		font-size: 10px;
		color: #ffffff;
		transition: color 0.3s ease;
	}

	${(props) =>
		props.$open &&
		css`
			z-index: 10;
			background-color: #000000;

			${TextLabel} {
				color: #ffffff;
			}
		`}
`;

const StyledHint = styled.div`
	position: absolute;
	top: 0;
	left: 0;
	min-width: 250px;
	background-color: rgba(255, 255, 255, 0.9);
	border: 1px solid #ffffff;
	border-radius: 10px;
	z-index: 1000;
	pointer-events: all;
	opacity: 0;
	transition: opacity 0.2s ease;

	&.visible {
		opacity: 1;
	}
`;

const StyledHintItemImage = styled.div`
	position: absolute;
	top: -1px;
	left: -1px;
	width: 80px;
	height: 80px;
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 1001;
	pointer-events: none;
`;

const StyledHintTopContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 7px;
	height: 80px;
	padding: 17px 10px 0 90px;
`;

const StyledTagsContainer = styled.div`
	display: flex;
	gap: 5px;
	white-space: nowrap;
`;

const StyledTag = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	width: fit-content;
	height: 20px;
	padding: 0 5px;
	background-color: #3ea2ff;
	border-radius: 6px;
	font-family: 'GT Walsheim Pro';
	font-style: normal;
	font-weight: 500;
	font-size: 12px;
	line-height: 14px;
	color: #ffffff;
	user-select: none;
`;

const StyledDropdown = styled.div`
	width: 100%;
	display: flex;
	flex-direction: column;
	padding: 20px;
	gap: 10px;
`;

const StyledSelectItem = styled.div`
	display: flex;
	align-items: center;
	gap: 10px;
	padding: 10px 10px 10px 0;
	/* border-bottom: 1px solid rgba(0, 0, 0, 0.1); */
	cursor: pointer;
	transition: padding 0.3s ease;

	${TextLabel} {
		color: #000000;
		font-size: 14px;
		font-weight: 400;
		transition: color 0.3s ease;
	}

	img {
		width: 20px;
		height: 20px;
		user-select: none;
		pointer-events: none;
		transition: filter 0.3s ease;
		filter: invert(100%);
	}
`;

const StyledSelectItemWrapper = styled.div`
	padding: 0;
	background-color: transparent;
	transition: background-color 0.3s ease;
	border-radius: 5px;

	&:hover {
		background-color: #000000;

		${StyledSelectItem} {
			padding: 10px;
			${TextLabel} {
				color: #ffffff;
			}

			img {
				filter: invert(0);
			}
		}
	}
`;

const StyledUsageLimitWrapper = styled.div`
	position: absolute;
	left: 10px;
	bottom: 10px;
	width: 60px;
	height: 8px;
	background-color: rgba(0, 0, 0, 0.75);
	padding: 2px;
	border-radius: 6px;
`;

const StyledUsageLimit = styled.div`
	height: 4px;
	border-radius: 6px;
	transition:
		width 0.3s ease,
		background-color 0.3s ease;
`;

export {
	StyledContainer,
	StyledCountContainer,
	StyledCross,
	StyledDropdown,
	StyledHint,
	StyledHintItemImage,
	StyledHintTopContainer,
	StyledItemImageContainer,
	StyledPlaceholderContainer,
	StyledQuickAccessButton,
	StyledSelectItem,
	StyledSelectItemWrapper,
	StyledTag,
	StyledTagsContainer,
	StyledUsageLimit,
	StyledUsageLimitWrapper,
	StyledWrapper,
};
